// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// The device-key keyring lives in localStorage under `zalmoxis.key.<deviceId>` =
// {priv, pub?, fingerprint?}. Crucially `deviceId` is `<userId>/<rand>` (server-assigned), so the
// slots are ALREADY namespaced by user — every key store/load filters on the `zalmoxis.key.<userId>/`
// prefix so a second account on the same browser/phone never loads (or is prompted for) another
// user's keys. Key backup (Phase 6) ferries these private keys across devices via an encrypted blob.

import { openPriv, sealPriv } from "./keystore";

export const KEY_PREFIX = "zalmoxis.key.";

// userKeyPrefix is the slot prefix owned by one user. `/` can't appear in a userId, so the prefix
// match is exact — no collision between e.g. "@a:x" and "@a:x.y".
export function userKeyPrefix(userId: string): string {
  return KEY_PREFIX + userId + "/";
}

// collectLocalKeyring returns every armored private key THIS USER holds on this browser (decrypting
// each slot's at-rest seal; legacy plaintext slots pass through openPriv unchanged).
export async function collectLocalKeyring(userId: string): Promise<string[]> {
  const privs: string[] = [];
  const prefix = userKeyPrefix(userId);
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(prefix)) continue;
    try {
      const v = JSON.parse(localStorage.getItem(k) || "{}");
      if (typeof v.priv === "string" && v.priv) privs.push(await openPriv(v.priv));
    } catch {
      /* skip malformed slots */
    }
  }
  return privs;
}

// persistRestoredKeys stores recovered private keys this user doesn't already hold, under the
// user's namespace (so the prefix filter picks them up) and SEALED at rest. Returns how many
// were new.
export async function persistRestoredKeys(userId: string, privs: string[]): Promise<number> {
  const existing = new Set(await collectLocalKeyring(userId));
  let added = 0;
  for (const priv of privs) {
    if (!priv || existing.has(priv)) continue;
    localStorage.setItem(`${userKeyPrefix(userId)}restored-${added}-${Date.now()}`, JSON.stringify({ priv: await sealPriv(priv) }));
    existing.add(priv);
    added += 1;
  }
  return added;
}
